import moment from 'moment'
import React, { useCallback, useMemo, useState } from 'react'
import TableLoader from '../../../components/TableLoader'
import useGetSalaryCalculation from '../../../hooks/query/useGetSalaryCalculation'
import { payoutDateFormat, yearMonthFormat } from '../../../shared/constants/dateFormats'
import { getDateScales } from '../utils/getDateScales'
import { getEarliestItemsDate } from '../utils/getEarliestItemsDate'
import { CompensationPopupProps, CompensationPopup } from './CompensationPopup'
import SalaryCalculationTable from './SalaryCalculationTable'

type SalaryCalculationContentProps = {
  practitionerReferenceId: string
}

const TABLE_SIZE = 12

const startDate = moment().subtract(TABLE_SIZE, 'months')
const endDate = startDate.clone().add(TABLE_SIZE, 'months')
const extendedEndDate = endDate.clone().add(TABLE_SIZE, 'months')

const SalaryCalculationContent = ({ practitionerReferenceId }: SalaryCalculationContentProps) => {
  const [popupState, setPopupState] =
    useState<Pick<CompensationPopupProps, 'anchorEl' | 'date' | 'isInitialCompensation'>>()

  const handlePopupClose = useCallback(() => {
    setPopupState(undefined)
  }, [setPopupState])

  const handleOpenCompensationPopup = useCallback(
    (revenueDate: string, currentCompensation?: number, isInitialCompensation = false) =>
      (event: React.MouseEvent<HTMLTableCellElement>) => {
        if (!currentCompensation && currentCompensation !== 0) {
          return
        }

        const date = moment(revenueDate, payoutDateFormat).format(yearMonthFormat)
        setPopupState({ anchorEl: event.currentTarget, date, isInitialCompensation })
      },
    [setPopupState]
  )

  const { data } = useGetSalaryCalculation({
    practitionerReferenceId,
    periodStart: startDate.toISOString(),
    periodEnd: extendedEndDate.toISOString(),
  })

  const { payoutDateScale, revenueDateScale } = useMemo(() => {
    const startDateForCalculation = data?.length ? moment(getEarliestItemsDate(data)) : startDate
    const endDateForCalculation = data?.length
      ? moment(getEarliestItemsDate(data)).add(TABLE_SIZE + 1, 'months')
      : endDate

    return getDateScales(startDateForCalculation, endDateForCalculation)
  }, [data])

  return (
    <>
      {data ? (
        <SalaryCalculationTable
          salaryCalculationItems={data}
          payoutDateScale={payoutDateScale}
          revenueDateScale={revenueDateScale}
          getRevenueColumnClickHandler={handleOpenCompensationPopup}
        />
      ) : (
        <TableLoader />
      )}

      {popupState ? (
        <CompensationPopup
          date={popupState.date}
          anchorEl={popupState.anchorEl}
          isInitialCompensation={popupState.isInitialCompensation}
          onClose={handlePopupClose}
          practitionerReferenceId={practitionerReferenceId}
        />
      ) : null}
    </>
  )
}

export default SalaryCalculationContent
